import type { TTiclickTableFilter } from '@src/design-system/molecules/ticlick-table/hooks/use-table-filters';
import { STATIC_FILTER_ENTITY_TYPES } from '@shared/features/views/components/entities-filters/config';
import { splitDateRangeFacetKey } from './split-date-range-facet-key';
import { FILTER_KIND } from '../types/filter-config';
import { booleanFilterToken, isNoneFilterValue } from './filter-none';
import { getFilterConfigByFacetName } from './get-filter-config-by-facet-name';

type TTransformedFilter = {
  field: string | number;
  value: string | (string | number | null)[];
  entityType?: (typeof STATIC_FILTER_ENTITY_TYPES)[keyof typeof STATIC_FILTER_ENTITY_TYPES];
};

const overrideSomeFacets = (
  filters?: TTiclickTableFilter['staticFilters']
): TTransformedFilter[] => {
  const result = (filters || []).flatMap((f): TTransformedFilter[] => {
    // None is stored as `null`. The leads query still sends the text `null`
    // (`gender=null`) because qs skips real nulls.
    if (isNoneFilterValue(f.value)) {
      if (f.entityType === STATIC_FILTER_ENTITY_TYPES.date) {
        const [beforeFieldName] = splitDateRangeFacetKey(f.field);
        return [
          {
            ...f,
            field: beforeFieldName.replace(/_before$/, ''),
            value: ['null'],
          },
        ];
      }
      return [{ ...f, value: ['null'] }];
    }

    if (f.entityType === STATIC_FILTER_ENTITY_TYPES.date) {
      const [beforeFieldName, afterFieldName] = splitDateRangeFacetKey(f.field);
      const [beforeValue, afterValue] = splitDateRangeFacetKey(String(f.value));

      const isBeforeValueUndefined = beforeValue === 'undefined';
      const isAfterValueUndefined = afterValue === 'undefined';

      return [
        ...(isBeforeValueUndefined
          ? []
          : [
              {
                ...f,
                field: beforeFieldName,
                value: beforeValue,
                entityType: STATIC_FILTER_ENTITY_TYPES.date,
              },
            ]),
        ...(isAfterValueUndefined
          ? []
          : [
              {
                ...f,
                field: afterFieldName,
                value: afterValue,
                entityType: STATIC_FILTER_ENTITY_TYPES.date,
              },
            ]),
      ];
    }

    if (f.entityType === STATIC_FILTER_ENTITY_TYPES.leadSource) {
      const hasNone = f.value.includes(null);
      const withoutNone = f.value.filter((value) => value !== null);

      return [
        ...(withoutNone.includes('lead_form')
          ? [{ ...f, field: 'is_manual', value: ['true'] }]
          : []),

        ...(withoutNone.some((v) => v !== 'lead_form')
          ? [
              {
                ...f,
                field: 'calendly_event_type',
                value: withoutNone.filter((v) => v !== 'lead_form') as string[],
              },
            ]
          : []),

        ...(hasNone ? [{ ...f, value: ['null'] }] : []),
      ];
    }

    const filterConfig = getFilterConfigByFacetName(String(f.field));
    if (
      filterConfig?.kind === FILTER_KIND.simple &&
      filterConfig.booleanValues
    ) {
      return f.value.map((token) => {
        const mapped = booleanFilterToken(String(token));
        return {
          ...f,
          field: mapped.field,
          value: [mapped.value],
        };
      });
    }

    if (f.value.includes(null)) {
      return [
        {
          ...f,
          value: f.value.map((value) => (value === null ? 'null' : value)),
        },
      ];
    }

    return [f];
  });

  return result;
};

export const convertActiveFiltersToApiParams = (
  filters?: TTiclickTableFilter['staticFilters']
) => {
  const finalFilters = overrideSomeFacets(filters);

  const selectedFiltersObj = Object.fromEntries(
    (finalFilters || []).map((filter) => {
      const { value, field } = filter;

      const mappedValue = Array.isArray(value) ? value.map((v) => v) : value;

      return [field, mappedValue];
    })
  );
  return selectedFiltersObj;
};
